import { Component, Input, Pipe, PipeTransform, ViewChild } from '@angular/core'
import { MatTableDataSource } from '@angular/material/table'
import { NGXLogger } from 'ngx-logger'
import { catchError, first, tap } from 'rxjs/operators'
import { of } from 'rxjs'
import { C4PaymentMethod, CancellationInfo, HttpService } from '@api/http.service'
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatDialog } from '@angular/material/dialog'
import { Store } from '@ngrx/store'
import { Subscriptions } from '../../../utils/subscriptions'
import { selectProfileState } from '../../../account/user-profile/user-profile.state'
import { OrderElement } from '../../data/orderelement'
import { environment } from 'src/environments/environment'
import { DatePipe } from '@angular/common'
import { TranslateService } from '@ngx-translate/core'
import { StatusObject } from '../../../utils/components/interfaces/models'
import { MatSidenav } from '@angular/material/sidenav'
import { ApiService } from '@api/api.service'
import { ChangePaymentMethodDialogComponent } from '../change-payment-method-dialog/change-payment-method-dialog.component'
import { OrderPaymentMethodActions } from '../../states/payment-methods/payment-methods.actions'
import { MatSort } from '@angular/material/sort'
import {ComplaintFormComponent} from "../complaint-form/complaint-form.component";

@Pipe({
  name: 'sortOrder',
})
export class SortOrderPipe implements PipeTransform {

  transform(array: OrderElement[]): OrderElement[] {
    const sorted = array.sort((a, b) => {
      const dateA = a.type === 'ORDER' ? this.parseOrderDate(a.startDate) : this.parseArticleDate(a.startDate)
      const dateB = b.type === 'ORDER' ? this.parseOrderDate(b.startDate) : this.parseArticleDate(b.startDate)
      return dateB.getTime() - dateA.getTime()
    })
    return sorted
  }

  parseOrderDate(dateStr: string): Date {
    const day = dateStr.slice(6)
    const month = dateStr.slice(4, 6)
    const year = dateStr.slice(0, 4)
    return new Date(+year, +month - 1, +day)
  }

  parseArticleDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split('.')
    return new Date(+year, +month - 1, +day)
  }
}

@Component({
  selector: 'cover-orders-list[dataSource]',
  templateUrl: './orders-list.component.html',
  styleUrls: ['./orders-list.component.scss'],
})
export class OrdersListComponent {
  @Input() dataSource!: MatTableDataSource<OrderElement>
  @ViewChild('sidenav') sidenav: MatSidenav | undefined

  orderStatuses: StatusObject = {
    ACTIVE: '',
    ORDERED: '',
    CANCELLED: '',
    DONE: '',
    OPEN: '',
    INACTIVE: '',
    PENDING: '',
  }
  invoicesCurrentlyDownloading: string[] = []
  articlesCurrentlyDownloading: string[] = []
  promotionsCurrentlyDownloading: number[] = []
  cancellationIsLoading = false
  isRevocationLoading: boolean = false
  allowChangePaymentMethod: boolean = environment.allowChangePaymentMethod
  hasCancellationFeature: boolean = !!environment.features?.cancellation
  hasInvoiceDownloadFeature: boolean = !!environment.features?.invoiceDownload
  hasComplaintFeature: boolean = !!environment.features?.complaint
  canRevocate: boolean = environment.features?.revocation
  revocationCustomLink: string | null = environment.revocationCustomLink

  environment = environment
  c4SessionId: string = ''
  adrGr: string = ''
  customerInfo: Omit<CancellationInfo, 'attributeId' | 'attributeType'> | undefined
  paymentMethods: C4PaymentMethod[] = []
  private readonly subscriptions: Subscriptions = new Subscriptions(true)

  constructor(
    private readonly logger: NGXLogger,
    private readonly http: HttpService,
    private readonly snackbar: MatSnackBar,
    private readonly dialog: MatDialog,
    private readonly store: Store,
    private readonly datePipe: DatePipe,
    private readonly translate: TranslateService,
    private readonly api: ApiService,
  ) {
    this.subscriptions.plusOne = this.translate
      .get('ORDERS.STATUS')
      .pipe(
        tap((statusObject: StatusObject) => {
          this.orderStatuses = statusObject
        }),
      )
      .subscribe()
    this.subscriptions.plusOne = this.store
      .select(selectProfileState.customerInformationState)
      .pipe(
        tap(customer => {
          this.customerInfo = {
            customerNr: customer.customerNumber,
            customerSubNr: customer.customerSubNumber,
            email: customer.email,
          }
        }),
      )
      .subscribe()
    this.subscriptions.plusOne = store
      .select(selectProfileState.c4SessionId)
      .pipe(
        tap(sessionId => {
          this.c4SessionId = sessionId
        }),
      )
      .subscribe()

    this.subscriptions.plusOne = store.select(selectProfileState.paymentMethodsState).subscribe(paymentMethods => {
      this.paymentMethods = paymentMethods
    })
  }

  expandedElement: OrderElement | null | undefined

  onClose(): void {
    this.expandedElement = null
  }

  openDetailView(element: any): void {
    this.expandedElement = element
  }

  cancel(element: OrderElement): void {
    this.api
      .getCancellationDialog()
      .pipe(
        first(),
        tap(initiateCancel => {
          if (initiateCancel === 'true') {
            this.cancellationIsLoading = true
            this.api
              .cancelSubscription(element.attributeId, element.type)
              .pipe(
                first(),
                tap(_ => {
                  this.cancellationIsLoading = false
                  this.sidenav?.close()
                }),
                catchError(err => {
                  this.cancellationIsLoading = false
                  return of(err)
                }),
              )
              .subscribe()
          }
        }),
      )
      .subscribe()
  }

  revocate(element: OrderElement): void {
    this.api
      .getCancellationDialog()
      .pipe(
        first(),
        tap(initiateCancel => {
          if (initiateCancel === 'true') {
            this.isRevocationLoading = true
            this.api
              .revocateSubscription(element)
              .pipe(
                first(),
                tap(_ => {
                  this.isRevocationLoading = false
                }),
                catchError(err => {
                  this.isRevocationLoading = false
                  return of(err)
                }),
              )
              .subscribe()
          }
        }),
      )
      .subscribe()
  }

  downloadInvoices(element: OrderElement): void {
    this.invoicesCurrentlyDownloading.push(element.uuid)
    let attributeIdToDownload = element.type !== 'ORDER' ? element.attributeId : element.uuid
    this.api
      .downloadInvoice(this.c4SessionId, attributeIdToDownload)
      .pipe(
        first(),
        tap(_ => {
          this.invoicesCurrentlyDownloading = this.invoicesCurrentlyDownloading.filter(entry => entry !== element.uuid)
        }),
        catchError(err => {
          this.invoicesCurrentlyDownloading = this.invoicesCurrentlyDownloading.filter(entry => entry !== element.uuid)
          return of(err)
        }),
      )
      .subscribe()
  }

  downloadArticle(downloadUrl: string, promotionIndex?: number, orderUUID?: string): void {
    if (promotionIndex !== undefined) {
      this.promotionsCurrentlyDownloading.push(promotionIndex)
    } else if (orderUUID) {
      this.articlesCurrentlyDownloading.push(orderUUID)
    }
    this.api
      .downloadArticle(downloadUrl)
      .pipe(
        first(),
        tap(_ => {
          if (promotionIndex !== undefined) {
            this.promotionsCurrentlyDownloading = this.promotionsCurrentlyDownloading.filter(
              entry => entry !== promotionIndex,
            )
          } else if (orderUUID) {
            this.articlesCurrentlyDownloading = this.articlesCurrentlyDownloading.filter(entry => entry !== orderUUID)
          }
        }),
        catchError(error => {
          if (promotionIndex !== undefined) {
            this.promotionsCurrentlyDownloading = this.promotionsCurrentlyDownloading.filter(
              entry => entry !== promotionIndex,
            )
          } else if (orderUUID) {
            this.articlesCurrentlyDownloading = this.articlesCurrentlyDownloading.filter(entry => entry !== orderUUID)
          }
          return of(error)
        }),
      )
      .subscribe()
  }

  mapSubscriptionsStatus(value: string): string {
    switch (value) {
      case 'CANCELED':
        return this.orderStatuses.CANCELLED
      case 'ACTIVE':
        return this.orderStatuses.ACTIVE
      case 'ORDERED':
        return this.orderStatuses.ORDERED
      case 'INACTIVE':
        return this.orderStatuses.INACTIVE
      case 'PENDING':
        return this.orderStatuses.PENDING
      default:
        return value
    }
  }

  mapArticleStatus(value: string): string {
    switch (value) {
      case 'OPEN':
        return this.orderStatuses.OPEN
      case 'DONE':
        return this.orderStatuses.DONE
      default:
        return value
    }
  }

  openPaymentMethods(element?: OrderElement | null): void {
    let paymentMethodsToChoose = this.paymentMethods.filter(it => it.ZAHLWEG_ID !== element?.paymentId)
    this.subscriptions.plusOne = this.dialog
      .open(ChangePaymentMethodDialogComponent, {
        data: paymentMethodsToChoose,
      })
      .afterClosed()
      .subscribe(paymentMethodSelected => {
        if (paymentMethodSelected)
          this.store.dispatch(OrderPaymentMethodActions.trySaveChanges({ payload: paymentMethodSelected }))
      })
  }

  /**
   * Formats date strings like "20231121" to "21.11.2023" for example.
   * Used for OrderElement objects of attributeType "ORDER". Other types already deliver date in dd.MM.yyyy format.
   * @param dateStr e.g.20231121
   * @return e.g. 21.11.2023
   */
  formatArticleDate(dateStr: string): string | null {
    let year = parseInt(dateStr.substring(0, 4))
    let month = parseInt(dateStr.substring(4, 6)) - 1
    let day = parseInt(dateStr.substring(6, 8))

    let date = new Date(year, month, day)
    return this.datePipe.transform(date, 'dd.MM.yyyy')
  }

  makeComplaint(order: any): void {
    this.dialog.open(ComplaintFormComponent, {
      data: order,
    })
  }

  canChangeOrderPaymentMethod(order: OrderElement): boolean {
    const { type, paymentId } = order

    return (
      (type.toUpperCase() === 'SUBSCR' || type.toUpperCase() === 'SUBSCRARC') &&
      !!paymentId &&
      (paymentId[0] === 'F' || paymentId[0] === 'S')
    )
  }
}
